import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  useCloseSupportTicket,
  useReplySupportTicket,
  useSupportTicket,
} from '@/api/queries/support';
import { useUIStore } from '@/store/ui';
import { useLocale } from '@/hooks/useLocale';
import { formatDateTime } from '@/utils/date';
import { isAPIError } from '@/api/error';
import { paths } from '@/routes/paths';
import styles from './styles.module.css';

export function SupportTicketPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const pushToast = useUIStore((s) => s.pushToast);

  const ticket = useSupportTicket(id ?? null);
  const reply = useReplySupportTicket(id ?? '');
  const close = useCloseSupportTicket(id ?? '');

  const [draft, setDraft] = useState('');

  if (!id) {
    navigate(paths.support, { replace: true });
    return <></>;
  }

  const isClosed =
    ticket.data?.status === 'closed' || ticket.data?.status === 'resolved';

  const handleSend = async () => {
    const body = draft.trim();
    if (body.length < 1) return;
    try {
      await reply.mutateAsync({ body });
      setDraft('');
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  const handleClose = async () => {
    try {
      await close.mutateAsync();
      pushToast({ body: t('support.closed'), variant: 'success' });
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer paddingX="md">
        <TopBar back title={ticket.data?.subject ?? t('support.ticket')} />

        {ticket.isLoading && (
          <div style={{ display: 'grid', placeItems: 'center', padding: 32 }}>
            <Spinner />
          </div>
        )}

        {ticket.data && (
          <>
            <Card variant="light" padding="md">
              <div className={styles.statusLine}>
                <span>{t('support.status_label')}:</span>
                <strong>{t(`support.status.${ticket.data.status}`)}</strong>
              </div>
            </Card>

            <div className={styles.thread}>
              {ticket.data.messages.map((m) => (
                <div
                  key={m.id}
                  className={[
                    styles.msg,
                    m.author_kind === 'user' ? styles.msgMine : styles.msgTheirs,
                  ].join(' ')}
                >
                  <div className={styles.msgMeta}>
                    <strong>{m.author_name}</strong>
                    <span>{formatDateTime(m.created_at, locale)}</span>
                  </div>
                  <div className={styles.msgBody}>{m.body}</div>
                </div>
              ))}
            </div>

            {!isClosed ? (
              <Card variant="light" padding="md">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  placeholder={t('support.reply_placeholder')}
                  className={styles.textarea}
                />
                <div className={styles.actions}>
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={handleClose}
                    loading={close.isPending}
                  >
                    {t('support.close_ticket')}
                  </Button>
                  <Button
                    variant="inverted"
                    size="md"
                    onClick={handleSend}
                    loading={reply.isPending}
                    disabled={!draft.trim()}
                  >
                    {t('support.send')}
                  </Button>
                </div>
              </Card>
            ) : (
              <Card variant="light" padding="md">
                <p style={{ margin: 0, color: 'var(--color-text-on-dark-muted)' }}>
                  {t('support.closed_notice')}
                </p>
              </Card>
            )}
          </>
        )}
      </PageContainer>
    </AppShell>
  );
}
